import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: #333;
  text-align: center;
`;

const AgentSelector = styled.div`
  margin-bottom: 2rem;
`;

const SelectorTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #555;
`;

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
`;

const SelectButton = styled.button`
  width: 100%;
  padding: 1rem;
  border: 2px solid #ddd;
  border-radius: 8px;
  background-color: #fff;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #007bff;
    background-color: #f0f8ff;
  }
`;

const SelectDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 0 0 8px 8px;
  background-color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
`;

const SelectOption = styled.div`
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #f0f8ff;
  }
  
  &.selected {
    background-color: #e3f2fd;
    border-left: 4px solid #007bff;
  }
`;

const OptionName = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 0.25rem;
`;

const OptionCategory = styled.div`
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.25rem;
`;

const OptionDescription = styled.div`
  font-size: 0.75rem;
  color: #888;
  line-height: 1.3;
`;

const ChatContainer = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  height: 600px;
  display: flex;
  flex-direction: column;
`;

const ChatHeader = styled.div`
  background-color: #f8f9fa;
  padding: 1rem;
  border-bottom: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ChatTitle = styled.h2`
  font-size: 1.2rem;
  color: #333;
`;

const ClearButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: #c82333;
  }
`;

const MessageList = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  background-color: #f5f5f5;
`;

const Message = styled.div`
  margin-bottom: 1rem;
  max-width: 80%;
  
  &.user {
    margin-left: auto;
    background-color: #e3f2fd;
    border-radius: 18px 18px 4px 18px;
  }
  
  &.agent {
    margin-right: auto;
    background-color: #fff;
    border-radius: 18px 18px 18px 4px;
  }
`;

const MessageContent = styled.div`
  padding: 1rem;
`;

const MessageSender = styled.div`
  font-size: 0.8rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: ${props => props.isUser ? '#007bff' : '#666'};
`;

const MessageText = styled.p`
  line-height: 1.4;
  color: #333;
`;

const ChatInput = styled.div`
  padding: 1rem;
  border-top: 1px solid #ddd;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const InputOptions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const OptionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #e3f2fd;
    border-color: #007bff;
  }
`;

const InputField = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 24px;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const InputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const SendButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 24px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #0069d9;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const AgentPage = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get('/api/v1/agent/list');
      const agentList = response.data.agents;
      setAgents(agentList);
      // 自动选择第一个智能体作为默认选项
      if (agentList.length > 0 && !selectedAgent) {
        setSelectedAgent(agentList[0]);
      }
    } catch (error) {
      console.error('获取智能体列表失败:', error);
    }
  };

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
    setMessages([]);
    setShowDropdown(false);
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event) => {
      const selectContainer = document.querySelector('.select-container');
      if (selectContainer && !selectContainer.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !selectedAgent || loading) return;

    const userMessage = {
      sender: 'user',
      text: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await axios.post('/api/v1/agent/chat', {
        agent_name: selectedAgent.name,
        message: inputMessage
      });

      const agentMessage = {
        sender: 'agent',
        text: response.data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, agentMessage]);
    } catch (error) {
      console.error('发送消息失败:', error);
      const errorMessage = {
        sender: 'agent',
        text: '抱歉，无法处理您的请求，请稍后重试。',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearMessages = () => {
    setMessages([]);
  };

  return (
    <PageContainer>
      <PageTitle>智能体中心</PageTitle>
      
      <AgentSelector>
        <SelectorTitle>选择智能体</SelectorTitle>
        <SelectContainer className="select-container">
          <SelectButton onClick={toggleDropdown}>
            <span>{selectedAgent ? selectedAgent.name : '请选择智能体'}</span>
            <span>{showDropdown ? '▼' : '▶'}</span>
          </SelectButton>
          {showDropdown && (
            <SelectDropdown>
              {agents.map((agent, index) => (
                <SelectOption
                  key={index}
                  className={selectedAgent?.name === agent.name ? 'selected' : ''}
                  onClick={() => handleAgentSelect(agent)}
                >
                  <OptionName>{agent.name}</OptionName>
                  <OptionCategory>{agent.category}</OptionCategory>
                  <OptionDescription>{agent.description || '无描述'}</OptionDescription>
                </SelectOption>
              ))}
            </SelectDropdown>
          )}
        </SelectContainer>
      </AgentSelector>

      {selectedAgent && (
        <ChatContainer>
          <ChatHeader>
            <ChatTitle>与 {selectedAgent.name} 对话</ChatTitle>
            <ClearButton onClick={handleClearMessages}>清空对话</ClearButton>
          </ChatHeader>
          
          <MessageList>
            <AnimatePresence>
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Message className={message.sender}>
                    <MessageContent>
                      <MessageSender isUser={message.sender === 'user'}>
                        {message.sender === 'user' ? '我' : selectedAgent.name}
                      </MessageSender>
                      <MessageText>{message.text}</MessageText>
                    </MessageContent>
                  </Message>
                </motion.div>
              ))}
            </AnimatePresence>
          </MessageList>
          
          <ChatInput>
            <InputOptions>
              <OptionButton onClick={() => setInputMessage('你好，能介绍一下你自己吗？')}>
                <span>👋</span>
                自我介绍
              </OptionButton>
              <OptionButton onClick={() => setInputMessage('你能帮我解决什么问题？')}>
                <span>💡</span>
                能力范围
              </OptionButton>
              <OptionButton onClick={() => setInputMessage('如何使用你的功能？')}>
                <span>📖</span>
                使用指南
              </OptionButton>
              <OptionButton onClick={() => setInputMessage('你有什么专业技能？')}>
                <span>🔧</span>
                专业技能
              </OptionButton>
              <OptionButton onClick={() => setInputMessage('能给我一个例子吗？')}>
                <span>📝</span>
                示例
              </OptionButton>
            </InputOptions>
            <InputContainer>
              <InputField
                type="text"
                placeholder="输入消息..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              />
              <SendButton
                onClick={handleSendMessage}
                disabled={loading || !inputMessage.trim()}
              >
                {loading ? '发送中...' : '发送'}
              </SendButton>
            </InputContainer>
          </ChatInput>
        </ChatContainer>
      )}
    </PageContainer>
  );
};

export default AgentPage;
